import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { TopNav, RoleBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Search, ShieldCheck, Calendar, Upload, AlertCircle } from "lucide-react";
import { warn } from "@/lib/logger";

export default function PlayersBrowser() {
  const { user } = useAuth();
  const [players, setPlayers] = useState([]);
  const [info, setInfo] = useState(null);
  const [role, setRole] = useState("P");
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    api.get(`/players`, { params: { role, search: search || undefined } })
      .then((r) => setPlayers(r.data))
      .catch((err) => {
        warn("players load failed:", err);
        toast.error("Errore caricamento");
      });
  }, [role, search]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/listone/info")
      .then((r) => setInfo(r.data))
      .catch((err) => warn("listone info load failed:", err));
  }, []);

  const fmtDate = (iso) => {
    if (!iso) return null;
    try { return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" }); }
    catch { return null; }
  };
  const updated = fmtDate(info?.updated_at);
  const isSuperadmin = user?.role !== "guest"; // every registered room-creator can upload
  const noListone = info?.count === 0;

  // Empty-state view when no Listone has been uploaded yet
  if (noListone) {
    return (
      <div>
        <TopNav />
        <div className="max-w-2xl mx-auto px-4 py-16">
          <div className="glass rounded-2xl p-10 text-center" data-testid="no-listone-state">
            <div className="w-16 h-16 rounded-2xl bg-amber-500/15 border border-amber-500/40 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-amber-400" />
            </div>
            <div className="label-eyebrow text-amber-400 mb-2">Listone non caricato</div>
            <h1 className="text-3xl font-black tracking-tight mb-3">Nessun calciatore disponibile</h1>
            <p className="text-slate-400 mb-8 leading-relaxed">
              Per avviare le aste è necessario caricare il Listone ufficiale dei calciatori.
              {isSuperadmin
                ? " Vai alla pagina di gestione per caricare un file CSV, Excel o PDF (compatibile con Fantacalcio.it)."
                : " Chiedi al creatore della sala di caricarlo."}
            </p>
            {isSuperadmin ? (
              <Link to="/admin/listone">
                <Button data-testid="upload-listone-btn" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-black h-12 px-8">
                  <Upload className="w-5 h-5 mr-2" />Carica il Listone
                </Button>
              </Link>
            ) : (
              <div className="text-xs text-slate-500">Solo un creatore di sala registrato può caricare il listone.</div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <TopNav />
      <div className="max-w-6xl mx-auto px-4 py-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
          <div>
            <div className="label-eyebrow mb-2">Listone ufficiale Serie A</div>
            <h1 className="text-4xl font-black tracking-tight">Calciatori</h1>
            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
              {updated && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 font-bold" data-testid="listone-badge">
                  <Calendar className="w-3 h-3" />Listone aggiornato al {updated}
                </span>
              )}
              <span className="text-slate-400">{info?.count ?? players.length} calciatori totali</span>
            </div>
          </div>
          {user?.role !== "guest" && (
            <Link to="/admin/listone">
              <Button data-testid="go-listone-admin" variant="outline" className="border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10">
                <ShieldCheck className="w-4 h-4 mr-2" />Gestisci Listone
              </Button>
            </Link>
          )}
        </div>

        <div className="glass rounded-2xl p-5 mb-6 space-y-3">
          <Tabs value={role} onValueChange={setRole}>
            <TabsList className="bg-slate-900/60 border border-white/10">
              {[["P","Portieri"],["D","Difensori"],["C","Centrocampisti"],["A","Attaccanti"]].map(([k,l]) => (
                <TabsTrigger key={k} value={k} data-testid={`role-tab-${k}`} className="data-[state=active]:bg-emerald-500 data-[state=active]:text-slate-950 font-bold">{l}</TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-3.5 text-slate-500" />
            <Input data-testid="players-search" placeholder="Cerca calciatore…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 bg-slate-900/60 border-white/10 h-11" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="players-grid">
          {players.map((p) => (
            <div key={p.id} className="glass rounded-xl p-4 flex items-center gap-3 hover:border-emerald-500/40 transition-all">
              <RoleBadge role={p.role} className="w-11 h-11 text-base" />
              <div className="flex-1 min-w-0">
                <div className="font-bold truncate">{p.name}</div>
                <div className="text-xs text-slate-400">{p.team}</div>
              </div>
              <div className="font-mono font-black text-emerald-400 text-lg">{p.quotation}</div>
            </div>
          ))}
          {players.length === 0 && <div className="col-span-full text-center text-slate-500 py-10">Nessun calciatore trovato.</div>}
        </div>
      </div>
    </div>
  );
}
